import React from "react";
import { Helmet } from "react-helmet-async";
import { MdPhone, MdLocationOn } from "react-icons/md";

import styles from "./StoreDetails.module.scss";
import { useStoreDetails } from "./useStoreDetails";
import { PageHeader } from "../../../common/components/pageHeader/PageHeader";
import { ScheduleList } from "../../../common/components/scheduleList/ScheduleList";
import { getClusterImageUrl } from "../../../common/utils/functions/getImageUri";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const getMapEmbedUrl = (store: {
  address?: {
    value?: string;
    latitude?: string | number;
    longitude?: string | number;
  };
  city?: { value?: string };
}): string | null => {
  const lat = store.address?.latitude;
  const lng = store.address?.longitude;
  const query =
    lat && lng
      ? `${lat},${lng}`
      : [store.address?.value, store.city?.value].filter(Boolean).join(", ");

  if (!query) return null;

  if (GOOGLE_MAPS_API_KEY) {
    return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(
      query,
    )}&zoom=16&language=ru`;
  }

  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed`;
};

export const StoreDetails: React.FC = () => {
  const { store, cluster, ready, vacancies } = useStoreDetails();

  if (!ready || !store) return null;

  const mapQuery =
    store.address?.latitude && store.address?.longitude
      ? `${store.address.latitude},${store.address.longitude}`
      : [store.address?.value, store.city?.value].filter(Boolean).join(", ");

  const addressLine = [store.address?.value, store.city?.value]
    .filter(Boolean)
    .join(", ");

  const hasImage = Boolean(store.files?.[0]);
  const mapEmbedUrl = getMapEmbedUrl(store);

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{`${store.name || "Точка"} — logmall`}</title>
        <meta
          name="description"
          content={store.description || store.name || "Точка на logmall"}
        />
      </Helmet>

      <PageHeader
        title={store.name}
        subtitle={addressLine}
        logo={
          cluster ? `https://${cluster.domain}/icons/logo512.png` : undefined
        }
      />

      <div
        className={`${styles.Layout} ${hasImage ? "" : styles.LayoutSingle}`}
      >
        {hasImage && cluster && (
          <img
            src={getClusterImageUrl(cluster, store.files![0])}
            alt={store.name}
            className={styles.Image}
            loading="lazy"
          />
        )}

        <div className={styles.Side}>
          {store.description && (
            <p className={styles.Description}>{store.description}</p>
          )}

          <div className={styles.Contacts}>
            {store.phone_number && (
              <a
                href={`tel:${store.phone_number.replace(/\s/g, "")}`}
                className={styles.Contact}
              >
                <MdPhone size={20} />
                <span>{store.phone_number}</span>
              </a>
            )}

            {mapQuery && (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.Contact}
              >
                <MdLocationOn size={20} />
                <span>Открыть в Google Maps</span>
              </a>
            )}
          </div>

          {store.schedule && store.schedule.length > 0 && (
            <section className={styles.Block}>
              <h2 className={styles.BlockTitle}>Режим работы</h2>
              <ScheduleList schedule={store.schedule} />
            </section>
          )}
        </div>
      </div>

      {mapEmbedUrl && (
        <section className={styles.Section}>
          <h2 className={styles.BlockTitle}>На карте</h2>
          <iframe
            className={styles.Map}
            src={mapEmbedUrl}
            title={`${store.name} на карте`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </section>
      )}
    </div>
  );
};
